// Social Media AI — composes platform-specific posts (Instagram / Facebook /
// LinkedIn) from a single brief + optional kitchen image.
//
// Mirrors the source-of-truth at:
//   /var/www/kitchenos/portal-standalone.html
//     widget shell:   line 2358   (#w-portal-social-ai)
//     widget JS:      line 4385   (Portal Social Media AI block; saiHandle*,
//                                 saiTogglePlat, saiPublish, portalSocialAiGenerate)
//     legacy widget:  /var/www/kitchenos/retailer-social-ai.js (separate floating
//                     panel that talks to /api/retailer/:retailerId/social/*)
//
// Backend status (as of this extraction):
//   POST /api/social/generate exists at /var/www/kitchenos-api/modules/social.js:112
//     body  -> { platform: 'instagram'|'facebook'|'linkedin'|'tiktok', brief, extra? }
//     reply -> { ok, platform, text, model, usage }
//   The endpoint generates ONE platform per call, so this page fans out one
//   request per selected platform in parallel. If the endpoint 404s (e.g. on
//   environments where social.js isn't wired up), we fall back to a
//   deterministic local stub generator so the UX flow stays testable. A
//   banner makes the stub-mode obvious.
//
//   "Post now" is intentionally a stub — the real publish endpoint
//   (POST /api/social/posts/:id/publish) requires per-platform OAuth
//   credentials that the retailer hasn't necessarily connected; that flow
//   lives in Settings → Integrations and is out of scope here.

import { ApiError } from "@tkos/api-client";
import { iconMarkup } from "@tkos/shared-ui";

import { registerPage } from "../../shell/router";
import type { Page, PageContext, PageCleanup } from "../../shell/types";

import "./styles.css";

// ── Types ────────────────────────────────────────────────────────────────────

const PLATFORMS = ["instagram", "facebook", "linkedin"] as const;
type Platform = (typeof PLATFORMS)[number];

const PLATFORM_META: Record<Platform, { label: string; maxChars: number }> = {
  instagram: { label: "Instagram", maxChars: 2200 },
  facebook:  { label: "Facebook",  maxChars: 5000 },
  linkedin:  { label: "LinkedIn",  maxChars: 3000 },
};

const POST_TYPES = [
  { id: "new-install",   label: "New install",
    seed: "New kitchen installation complete — beautiful result for the customer." },
  { id: "before-after",  label: "Before/after",
    seed: "Before-and-after kitchen transformation we just finished." },
  { id: "showroom",      label: "Showroom promo",
    seed: "Showroom promotion — visit us this weekend for inspiration." },
  { id: "testimonial",   label: "Testimonial",
    seed: "Five-star customer testimonial about our design service." },
] as const;

type PostTypeId = (typeof POST_TYPES)[number]["id"];

interface ResultEntry {
  platform: Platform;
  /** Final, possibly user-edited copy. */
  text: string;
  /** True while a request is in-flight for this platform. */
  loading: boolean;
  /** Non-null when generation failed for this platform. */
  error: string | null;
  /** Whether the user has the inline editor open. */
  editing: boolean;
  /** True when this entry was produced by the local stub generator. */
  stubbed: boolean;
}

interface GenerateResponse {
  ok?: boolean;
  platform?: string;
  text?: string;
}

// ── Constants ────────────────────────────────────────────────────────────────

const MAX_FILE_BYTES = 5 * 1024 * 1024; // 5 MB
const ACCEPTED_MIME = ["image/jpeg", "image/png", "image/webp"];
const ACCEPTED_EXT = /\.(jpe?g|png|webp)$/i;

// ── Helpers ──────────────────────────────────────────────────────────────────

function esc(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function isAbortError(err: unknown): boolean {
  return err instanceof DOMException && err.name === "AbortError";
}

function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Strip the `data:image/xxx;base64,` prefix to get the raw base64 payload. */
function stripDataUrl(dataUrl: string): string {
  const comma = dataUrl.indexOf(",");
  return comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl;
}

/** Deterministic hash so the local stub returns stable copy per brief. */
function hash(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (h * 31 + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

// ── Local stub generator ─────────────────────────────────────────────────────
//
// Used only when /api/social/generate returns 404. Each platform has its own
// voice envelope so the UX flow is meaningfully testable without a backend.

const STUB_HASHTAGS_IG = [
  "#KitchenDesign", "#DreamKitchen", "#KitchenInspo", "#NewKitchen",
  "#HandmadeKitchens", "#ModernKitchen", "#UKKitchens", "#KitchenGoals",
];
const STUB_HASHTAGS_LI = [
  "#KitchenIndustry", "#InteriorDesign", "#HomeImprovement",
  "#CustomerExperience", "#CraftedInTheUK",
];

function pickRotating<T>(arr: readonly T[], count: number, seed: number): T[] {
  const out: T[] = [];
  for (let i = 0; i < count && arr.length > 0; i++) {
    const item = arr[(seed + i) % arr.length] as T;
    out.push(item);
  }
  return out;
}

function stubGenerate(platform: Platform, brief: string, hasImage: boolean): string {
  const seed = hash(brief + platform);
  const trimmed = brief.trim() || "A beautiful new kitchen we've just finished";
  const imageNote = hasImage ? " (see photo) " : " ";

  if (platform === "instagram") {
    const tags = pickRotating(STUB_HASHTAGS_IG, 6, seed).join(" ");
    return `${trimmed}${imageNote}✨\n\nTap to see more of what we do.\n\n${tags}`;
  }
  if (platform === "facebook") {
    return (
      `${trimmed}.${imageNote}We loved bringing this one together — every detail planned, every cabinet made to fit.\n\n` +
      `If you're thinking about a new kitchen, drop us a message or pop into the showroom — we'd love to talk through your space.`
    );
  }
  // linkedin
  return (
    `Another project handed over this week — ${trimmed.toLowerCase()}.${imageNote}\n\n` +
    `Behind every install is a team of designers, fitters and suppliers working in lockstep. ` +
    `It's the boring, careful work — accurate measurements, honest timelines, tight communication — ` +
    `that makes a kitchen worth being proud of.\n\n` +
    `${pickRotating(STUB_HASHTAGS_LI, 3, seed).join(" ")}`
  );
}

// ── Mount ────────────────────────────────────────────────────────────────────

const socialAiPage: Page = {
  name: "social-ai",
  label: "Social Media AI",
  icon: "",

  mount(container: HTMLElement, ctx: PageContext): PageCleanup {
    // ── State ───────────────────────────────────────────────────────────────
    const ac = new AbortController();
    let selectedPlatforms: Set<Platform> = new Set(["instagram", "facebook"]);
    let description = "";
    let postType: PostTypeId | null = null;
    let imageDataUrl: string | null = null;
    let imageName = "";
    let imageSize = 0;
    /** Created object URL used for preview thumbnail (so we can revoke it). */
    let imageObjectUrl: string | null = null;
    let results: ResultEntry[] = [];
    let isGenerating = false;
    /** True once we've discovered the backend isn't reachable, so we stay in stub mode. */
    let stubModeSticky = false;

    // ── DOM scaffold ────────────────────────────────────────────────────────

    const root = document.createElement("section");
    root.className = "social-page";
    root.innerHTML = `
      <header class="social-header">
        <div class="social-header__title">
          <div class="social-header__icon" aria-hidden="true">
            ${iconMarkup("link", { size: "md" })}
          </div>
          <div>
            <h1>Social Media AI</h1>
            <p>One brief, three platform-tailored posts. Pick channels, drop in a photo, hit Generate.</p>
          </div>
        </div>
        <span class="social-header__badge" aria-hidden="true">AI</span>
      </header>

      <div class="social-banner" data-role="stub-banner" hidden>
        ${iconMarkup("info", { size: "sm" })}
        <span>
          <strong>Live AI generation pending</strong> — showing example copy. Once
          <code>/api/social/generate</code> is reachable, this page will switch to live output.
        </span>
      </div>

      <section class="social-card">
        <div>
          <p class="social-section-label">Post to</p>
          <div class="social-platforms" data-role="platforms" role="group" aria-label="Select platforms">
            ${PLATFORMS.map((p) => `
              <button
                type="button"
                class="social-chip"
                data-plat="${p}"
                data-active="${selectedPlatforms.has(p) ? "true" : "false"}"
                aria-pressed="${selectedPlatforms.has(p) ? "true" : "false"}"
              >${esc(PLATFORM_META[p].label)}</button>
            `).join("")}
          </div>
        </div>

        <div>
          <p class="social-section-label">Image <span style="font-weight:500;text-transform:none;letter-spacing:0">(optional, JPG/PNG/WebP, max 5 MB)</span></p>
          <div class="social-drop" data-role="drop" data-has-image="false" data-dragging="false" role="button" tabindex="0" aria-label="Upload an image — drag and drop or click to browse">
            <div class="social-drop__placeholder">
              ${iconMarkup("upload", { size: "md" })}
              <span>Drop a kitchen image here or <em>click to upload</em> — AI will reference it in the post</span>
            </div>
            <div class="social-drop__preview">
              <img data-role="preview-img" src="" alt="Selected image preview" />
              <div style="flex:1;min-width:0;display:flex;flex-direction:column;gap:2px">
                <span class="social-drop__preview-name" data-role="preview-name"></span>
                <span class="social-drop__preview-meta" data-role="preview-meta"></span>
              </div>
              <button type="button" class="social-drop__remove" data-role="image-remove">${iconMarkup("close", { size: "xs" })} Remove</button>
            </div>
          </div>
          <input type="file" data-role="file-input" accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp" hidden />
        </div>

        <div>
          <p class="social-section-label">What's the post about?</p>
          <textarea
            class="social-textarea"
            data-role="description"
            rows="5"
            placeholder="Describe the post — e.g. 'New showroom display, handleless gloss white, modern feel. Open Sunday for viewings.'"
          ></textarea>
        </div>

        <div>
          <p class="social-section-label">Quick types</p>
          <div class="social-quick" data-role="quick-types">
            ${POST_TYPES.map((t) => `
              <button type="button" class="social-quick__btn" data-quick="${t.id}">${esc(t.label)}</button>
            `).join("")}
          </div>
        </div>

        <div class="social-actions">
          <button type="button" class="social-btn social-btn--primary" data-role="generate">
            ${iconMarkup("refresh", { size: "xs" })}
            Generate
          </button>
        </div>
      </section>

      <div class="social-results" data-role="results" aria-live="polite"></div>
    `;
    container.appendChild(root);

    // ── DOM refs ────────────────────────────────────────────────────────────
    const stubBanner       = root.querySelector<HTMLDivElement>("[data-role='stub-banner']")!;
    const platformsEl      = root.querySelector<HTMLDivElement>("[data-role='platforms']")!;
    const dropEl           = root.querySelector<HTMLDivElement>("[data-role='drop']")!;
    const fileInput        = root.querySelector<HTMLInputElement>("[data-role='file-input']")!;
    const previewImg       = root.querySelector<HTMLImageElement>("[data-role='preview-img']")!;
    const previewName      = root.querySelector<HTMLSpanElement>("[data-role='preview-name']")!;
    const previewMeta      = root.querySelector<HTMLSpanElement>("[data-role='preview-meta']")!;
    const removeImageBtn   = root.querySelector<HTMLButtonElement>("[data-role='image-remove']")!;
    const descriptionEl    = root.querySelector<HTMLTextAreaElement>("[data-role='description']")!;
    const quickTypesEl     = root.querySelector<HTMLDivElement>("[data-role='quick-types']")!;
    const generateBtn      = root.querySelector<HTMLButtonElement>("[data-role='generate']")!;
    const resultsEl        = root.querySelector<HTMLDivElement>("[data-role='results']")!;

    // ── Render helpers ──────────────────────────────────────────────────────

    function renderPlatformChips(): void {
      const chips = platformsEl.querySelectorAll<HTMLButtonElement>(".social-chip");
      chips.forEach((c) => {
        const p = c.dataset.plat as Platform | undefined;
        if (!p) return;
        const active = selectedPlatforms.has(p);
        c.dataset.active = active ? "true" : "false";
        c.setAttribute("aria-pressed", active ? "true" : "false");
      });
    }

    function setBannerVisible(v: boolean): void {
      stubBanner.hidden = !v;
    }

    function renderImagePreview(): void {
      if (imageDataUrl) {
        dropEl.dataset.hasImage = "true";
        previewImg.src = imageObjectUrl ?? imageDataUrl;
        previewName.textContent = imageName || "Image";
        previewMeta.textContent = formatBytes(imageSize);
      } else {
        dropEl.dataset.hasImage = "false";
        previewImg.removeAttribute("src");
        previewName.textContent = "";
        previewMeta.textContent = "";
      }
    }

    function renderResults(): void {
      if (results.length === 0) {
        resultsEl.innerHTML = "";
        return;
      }
      resultsEl.innerHTML = results.map(renderResultPanel).join("");
    }

    function renderResultPanel(r: ResultEntry): string {
      const meta = PLATFORM_META[r.platform];
      const count = r.text.length;
      const overLimit = count > meta.maxChars;
      const countLabel = `${count} / ${meta.maxChars} chars${overLimit ? " — over limit" : ""}`;

      let body: string;
      if (r.loading) {
        body = `<div class="social-result__loading">Generating ${esc(meta.label)} copy…</div>`;
      } else if (r.error) {
        body = `<div class="social-result__error">${esc(r.error)}</div>`;
      } else if (r.editing) {
        body = `<textarea class="social-result__edit" data-role="edit-area" data-plat="${r.platform}">${esc(r.text)}</textarea>`;
      } else {
        body = `<div class="social-result__body">${esc(r.text)}</div>`;
      }

      const actions = r.loading
        ? ""
        : `
          <div class="social-result__actions">
            ${r.editing
              ? `<button type="button" class="social-btn social-btn--ghost" data-action="save-edit" data-plat="${r.platform}">Save</button>
                 <button type="button" class="social-btn social-btn--ghost" data-action="cancel-edit" data-plat="${r.platform}">Cancel</button>`
              : `<button type="button" class="social-btn social-btn--ghost" data-action="edit" data-plat="${r.platform}">${iconMarkup("note", { size: "xs" })} Edit</button>
                 <button type="button" class="social-btn social-btn--ghost" data-action="copy" data-plat="${r.platform}">${iconMarkup("file", { size: "xs" })} Copy</button>
                 <button type="button" class="social-btn social-btn--ghost" data-action="regenerate" data-plat="${r.platform}">${iconMarkup("refresh", { size: "xs" })} Regenerate</button>
                 <button type="button" class="social-btn social-btn--ghost" data-action="post" data-plat="${r.platform}">Post now</button>`
            }
          </div>
        `;

      return `
        <article class="social-result" data-plat="${r.platform}">
          <header class="social-result__head">
            <span class="social-result__platform">${esc(meta.label)}${r.stubbed ? " · example" : ""}</span>
            <span class="social-result__count">${esc(countLabel)}</span>
          </header>
          ${body}
          ${actions}
        </article>
      `;
    }

    function setGenerating(v: boolean): void {
      isGenerating = v;
      generateBtn.disabled = v;
      generateBtn.textContent = v ? "Generating…" : "";
      if (!v) {
        // Re-add the icon + label.
        generateBtn.innerHTML = `
          ${iconMarkup("refresh", { size: "xs" })}
          Generate
        `;
      }
    }

    // ── Image handling ──────────────────────────────────────────────────────

    function clearImage(): void {
      imageDataUrl = null;
      imageName = "";
      imageSize = 0;
      if (imageObjectUrl) {
        URL.revokeObjectURL(imageObjectUrl);
        imageObjectUrl = null;
      }
      fileInput.value = "";
      renderImagePreview();
    }

    function acceptFile(file: File): void {
      const okMime = ACCEPTED_MIME.includes(file.type);
      const okExt = ACCEPTED_EXT.test(file.name);
      if (!okMime && !okExt) {
        ctx.showToast("Image must be JPG, PNG, or WebP", "error");
        return;
      }
      if (file.size > MAX_FILE_BYTES) {
        ctx.showToast(`Image too large — max ${formatBytes(MAX_FILE_BYTES)}`, "error");
        return;
      }

      // Preview via object URL (cheap), full data URL via FileReader (sent to backend).
      if (imageObjectUrl) URL.revokeObjectURL(imageObjectUrl);
      imageObjectUrl = URL.createObjectURL(file);
      imageName = file.name;
      imageSize = file.size;

      const reader = new FileReader();
      reader.onload = (): void => {
        const result = reader.result;
        imageDataUrl = typeof result === "string" ? result : null;
        renderImagePreview();
      };
      reader.onerror = (): void => {
        ctx.showToast("Could not read image", "error");
        clearImage();
      };
      reader.readAsDataURL(file);
      // Show preview immediately using the object URL even before FileReader
      // resolves — keeps the UI snappy.
      renderImagePreview();
    }

    // ── Event handlers ──────────────────────────────────────────────────────

    const onPlatformClick = (e: MouseEvent): void => {
      const btn = (e.target as HTMLElement | null)?.closest<HTMLButtonElement>(".social-chip");
      if (!btn) return;
      const p = btn.dataset.plat as Platform | undefined;
      if (!p || !PLATFORMS.includes(p)) return;
      if (selectedPlatforms.has(p)) {
        if (selectedPlatforms.size === 1) {
          ctx.showToast("Pick at least one platform", "info");
          return;
        }
        selectedPlatforms.delete(p);
      } else {
        selectedPlatforms.add(p);
      }
      renderPlatformChips();
    };

    const onDropClick = (): void => fileInput.click();
    const onDropKey = (e: KeyboardEvent): void => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        fileInput.click();
      }
    };
    const onDragOver = (e: DragEvent): void => {
      e.preventDefault();
      dropEl.dataset.dragging = "true";
    };
    const onDragLeave = (e: DragEvent): void => {
      e.preventDefault();
      dropEl.dataset.dragging = "false";
    };
    const onDrop = (e: DragEvent): void => {
      e.preventDefault();
      dropEl.dataset.dragging = "false";
      const file = e.dataTransfer?.files?.[0];
      if (file) acceptFile(file);
    };

    const onFileChange = (): void => {
      const file = fileInput.files?.[0];
      if (file) acceptFile(file);
    };

    const onRemoveImage = (e: MouseEvent): void => {
      e.stopPropagation();
      clearImage();
    };

    const onDescriptionInput = (): void => {
      description = descriptionEl.value;
    };

    const onQuickClick = (e: MouseEvent): void => {
      const btn = (e.target as HTMLElement | null)?.closest<HTMLButtonElement>(".social-quick__btn");
      if (!btn) return;
      const id = btn.dataset.quick as PostTypeId | undefined;
      const spec = POST_TYPES.find((t) => t.id === id);
      if (!spec) return;
      postType = spec.id;
      // Append to description if user has typed something, otherwise replace.
      if (description.trim()) {
        description = `${description.trim()}\n\n${spec.seed}`;
      } else {
        description = spec.seed;
      }
      descriptionEl.value = description;
      descriptionEl.focus();
    };

    const onGenerateClick = (): void => {
      void runGenerate();
    };

    const onResultsClick = (e: MouseEvent): void => {
      const btn = (e.target as HTMLElement | null)?.closest<HTMLButtonElement>("button[data-action]");
      if (!btn) return;
      const action = btn.dataset.action;
      const platform = btn.dataset.plat as Platform | undefined;
      if (!platform) return;
      const idx = results.findIndex((r) => r.platform === platform);
      if (idx < 0) return;
      const cur = results[idx];
      if (!cur) return;

      if (action === "edit") {
        results[idx] = { ...cur, editing: true };
        renderResults();
        const ta = resultsEl.querySelector<HTMLTextAreaElement>(
          `textarea[data-role='edit-area'][data-plat='${platform}']`,
        );
        ta?.focus();
        return;
      }

      if (action === "save-edit") {
        const ta = resultsEl.querySelector<HTMLTextAreaElement>(
          `textarea[data-role='edit-area'][data-plat='${platform}']`,
        );
        const newText = ta?.value ?? cur.text;
        results[idx] = { ...cur, editing: false, text: newText };
        renderResults();
        ctx.showToast("Edits saved", "success");
        return;
      }

      if (action === "cancel-edit") {
        results[idx] = { ...cur, editing: false };
        renderResults();
        return;
      }

      if (action === "copy") {
        void copyToClipboard(cur.text);
        return;
      }

      if (action === "regenerate") {
        void regenerateOne(platform);
        return;
      }

      if (action === "post") {
        ctx.showToast(
          `Posting to ${PLATFORM_META[platform].label} — coming soon. Connect ${PLATFORM_META[platform].label} in Settings → Integrations to enable direct publishing.`,
          "info",
        );
        return;
      }
    };

    async function copyToClipboard(text: string): Promise<void> {
      try {
        await navigator.clipboard?.writeText(text);
        ctx.showToast("Copied to clipboard", "success");
      } catch {
        ctx.showToast("Could not copy — select and copy manually", "error");
      }
    }

    // ── Backend / generation ────────────────────────────────────────────────

    function buildBrief(): string {
      const desc = description.trim();
      if (desc) return desc;
      if (postType) {
        const spec = POST_TYPES.find((t) => t.id === postType);
        if (spec) return spec.seed;
      }
      return "Write a compelling social media post for a UK kitchen retailer.";
    }

    async function callBackend(platform: Platform, brief: string): Promise<string> {
      // POST /api/social/generate (modules/social.js:112)
      //   body  -> { platform, brief, extra? }
      //   reply -> { ok, platform, text, model, usage }
      const extra = imageDataUrl
        ? `An image is attached to this post (filename: ${imageName}); reference it naturally as if describing the kitchen shown.`
        : undefined;

      const resp = await ctx.api<GenerateResponse>("/social/generate", {
        method: "POST",
        signal: ac.signal,
        body: JSON.stringify({
          platform,
          brief,
          ...(extra ? { extra } : {}),
          // imageBase64 is sent for forward-compat — current backend ignores it
          // but it's harmless and matches the master-doc contract.
          ...(imageDataUrl ? { imageBase64: stripDataUrl(imageDataUrl) } : {}),
        }),
      });
      const text = (resp?.text ?? "").trim();
      if (!text) throw new Error("Empty response from AI");
      return text;
    }

    async function generateForPlatform(platform: Platform, brief: string): Promise<{ text: string; stubbed: boolean }> {
      if (stubModeSticky) {
        return { text: stubGenerate(platform, brief, !!imageDataUrl), stubbed: true };
      }
      try {
        const text = await callBackend(platform, brief);
        return { text, stubbed: false };
      } catch (err) {
        if (isAbortError(err)) throw err;
        if (err instanceof ApiError && err.status === 404) {
          // Backend not wired in this environment — switch to stub mode
          // sticky for the remainder of the page lifecycle.
          if (!stubModeSticky) {
            stubModeSticky = true;
            setBannerVisible(true);
            console.info("[social-ai] /api/social/generate not available; using local stub generator.");
          }
          return { text: stubGenerate(platform, brief, !!imageDataUrl), stubbed: true };
        }
        throw err;
      }
    }

    async function runGenerate(): Promise<void> {
      if (isGenerating) return;
      const platforms = Array.from(selectedPlatforms);
      if (platforms.length === 0) {
        ctx.showToast("Pick at least one platform", "error");
        return;
      }
      const brief = buildBrief();
      if (!brief.trim() && !imageDataUrl) {
        ctx.showToast("Add a description or pick a quick type", "error");
        descriptionEl.focus();
        return;
      }

      // Seed loading entries — order matches selection order in PLATFORMS.
      results = PLATFORMS.filter((p) => selectedPlatforms.has(p)).map((platform) => ({
        platform,
        text: "",
        loading: true,
        error: null,
        editing: false,
        stubbed: false,
      }));
      renderResults();
      setGenerating(true);

      // Fan out one request per platform in parallel.
      const settled = await Promise.allSettled(
        platforms.map(async (p) => {
          const out = await generateForPlatform(p, brief);
          return { platform: p, ...out };
        }),
      );

      // Bail if the page was unmounted mid-request.
      if (ac.signal.aborted) return;

      let anyStub = false;
      let anyError = false;
      results = results.map((r) => {
        const result = settled.find((s) => {
          if (s.status === "fulfilled") return s.value.platform === r.platform;
          return false;
        });
        if (!result) return r;
        if (result.status === "fulfilled") {
          if (result.value.stubbed) anyStub = true;
          return {
            ...r,
            loading: false,
            text: result.value.text,
            stubbed: result.value.stubbed,
          };
        }
        // rejected — but we don't know which platform from this branch alone;
        // attribute by index.
        return r;
      });

      // Attribute rejections by the parallel array index (Promise.allSettled
      // preserves order).
      settled.forEach((s, i) => {
        if (s.status !== "rejected") return;
        if (isAbortError(s.reason)) return;
        anyError = true;
        const platform = platforms[i];
        if (!platform) return;
        const idx = results.findIndex((r) => r.platform === platform);
        if (idx < 0) return;
        const cur = results[idx];
        if (!cur) return;
        const message =
          s.reason instanceof Error ? s.reason.message : "Generation failed";
        results[idx] = { ...cur, loading: false, error: message };
      });

      setGenerating(false);
      renderResults();

      if (anyError) {
        ctx.showToast("Some platforms failed — see panels for details", "error");
      } else if (anyStub) {
        ctx.showToast("Showing example copy (live AI offline)", "info");
      } else {
        ctx.showToast("Posts generated", "success");
      }
    }

    async function regenerateOne(platform: Platform): Promise<void> {
      const idx = results.findIndex((r) => r.platform === platform);
      if (idx < 0) return;
      const cur = results[idx];
      if (!cur) return;
      results[idx] = { ...cur, loading: true, error: null, editing: false };
      renderResults();
      try {
        const out = await generateForPlatform(platform, buildBrief());
        if (ac.signal.aborted) return;
        const next = results.findIndex((r) => r.platform === platform);
        if (next >= 0) {
          const target = results[next];
          if (!target) return;
          results[next] = { ...target, loading: false, text: out.text, stubbed: out.stubbed };
        }
        renderResults();
        ctx.showToast(`${PLATFORM_META[platform].label} regenerated`, "success");
      } catch (err) {
        if (isAbortError(err)) return;
        const next = results.findIndex((r) => r.platform === platform);
        if (next >= 0) {
          const target = results[next];
          if (target) {
            const message = err instanceof Error ? err.message : "Regeneration failed";
            results[next] = { ...target, loading: false, error: message };
          }
        }
        renderResults();
        ctx.showToast("Could not regenerate", "error");
      }
    }

    // ── Wiring ──────────────────────────────────────────────────────────────

    platformsEl.addEventListener("click", onPlatformClick);
    dropEl.addEventListener("click", onDropClick);
    dropEl.addEventListener("keydown", onDropKey);
    dropEl.addEventListener("dragover", onDragOver);
    dropEl.addEventListener("dragleave", onDragLeave);
    dropEl.addEventListener("drop", onDrop);
    fileInput.addEventListener("change", onFileChange);
    removeImageBtn.addEventListener("click", onRemoveImage);
    descriptionEl.addEventListener("input", onDescriptionInput);
    quickTypesEl.addEventListener("click", onQuickClick);
    generateBtn.addEventListener("click", onGenerateClick);
    resultsEl.addEventListener("click", onResultsClick);

    // ── Initial render ──────────────────────────────────────────────────────

    renderPlatformChips();
    renderImagePreview();
    renderResults();

    // ── Cleanup ─────────────────────────────────────────────────────────────
    return () => {
      ac.abort();
      platformsEl.removeEventListener("click", onPlatformClick);
      dropEl.removeEventListener("click", onDropClick);
      dropEl.removeEventListener("keydown", onDropKey);
      dropEl.removeEventListener("dragover", onDragOver);
      dropEl.removeEventListener("dragleave", onDragLeave);
      dropEl.removeEventListener("drop", onDrop);
      fileInput.removeEventListener("change", onFileChange);
      removeImageBtn.removeEventListener("click", onRemoveImage);
      descriptionEl.removeEventListener("input", onDescriptionInput);
      quickTypesEl.removeEventListener("click", onQuickClick);
      generateBtn.removeEventListener("click", onGenerateClick);
      resultsEl.removeEventListener("click", onResultsClick);
      if (imageObjectUrl) {
        URL.revokeObjectURL(imageObjectUrl);
        imageObjectUrl = null;
      }
    };
  },
};

registerPage(socialAiPage);

export default socialAiPage;
